/* HOLDIT v2: a single owner in product-form.js handles submission.
 * File count drives service quantity. Shopify sets the actual price.
 * No automatic retries: a lost response can still mean the cart was updated.
 * Theme checks are UX validation, not server-side checkout enforcement.
 */
(() => {
  if (customElements.get('holdit-product-options')) return;
  const PACKS = {
    bolt: ['BOLT'], uber: ['UBER'], both: ['BOLT', 'UBER'],
    four: ['BOLT 1', 'UBER 1', 'BOLT 2', 'UBER 2'],
  };
  class HolditProductOptions extends HTMLElement {
    connectedCallback() {
      this.form = this.closest('form');
      if (!this.form) return;
      this.controller?.abort();
      this.controller = new AbortController();
      this.addEventListener('change', (event) => {
        if (event.target.matches('[data-holdit-print], [data-holdit-pack]')) this.renderFields();
        if (event.target.type === 'file') event.target.setCustomValidity('');
      }, { signal: this.controller.signal });
      this.form.enctype = 'multipart/form-data';
      this.renderFields();
      this.querySelector('[data-holdit-ready]').checked = true;
    }
    disconnectedCallback() { this.controller?.abort(); }
    wantsPrint() {
      return this.dataset.mode.includes('service') || this.querySelector('[data-holdit-print]')?.value === 'yes';
    }
    handleSubmit(event) {
      // Called synchronously by Horizon before its normal add-to-cart path.
      // Return true when handled, false to allow the normal no-print purchase.
      this.form.closest('.shopify-section')?.querySelector('holdit-phone-field')?.bindForm?.();
      if (!this.form.reportValidity()) return true;
      if (!this.wantsPrint()) return false;
      event.stopImmediatePropagation();
      void this.submit();
      return true;
    }
    renderFields() {
      const container = this.querySelector('[data-holdit-files]');
      if (!container) return;
      const enabled = this.wantsPrint();
      this.querySelector('[data-holdit-panel]').hidden = !enabled;
      const labels = enabled ? PACKS[this.querySelector('[data-holdit-pack]').value] : [];
      // Preserve only matching fields. Hidden, obsolete PDFs never enter the cart.
      const previous = new Map([...container.querySelectorAll('input')].map(input => [input.dataset.label, input]));
      container.replaceChildren();
      for (const label of labels) {
        const wrapper = document.createElement('label');
        wrapper.className = 'holdit-options__label';
        const title = document.createElement('span');
        title.textContent = `Încarcă PDF original ${label}`;
        const input = previous.get(label) || document.createElement('input');
        input.type = 'file'; input.accept = '.pdf,application/pdf'; input.required = true;
        input.dataset.label = label;
        // No name: only the paid service line receives these files in buildPayload().
        wrapper.append(title, input); container.append(wrapper);
      }
      const price = Number(this.dataset.servicePrice);
      const money = (cents) => new Intl.NumberFormat('ro-RO', { style:'currency', currency:this.dataset.currency || 'RON' }).format(cents / 100);
      const quantity = labels.length * 2;
      this.querySelector('[data-holdit-total]').textContent = enabled
        ? `Printare: ${money(price * quantity)} pentru ${quantity} ecusoane (${labels.length} PDF${labels.length > 1 ? '-uri' : ''}).`
        : '';
      this.showError('');
    }
    showError(message) {
      const error = this.querySelector('[data-holdit-error]');
      error.textContent = message; error.hidden = !message;
    }
    async validateFiles() {
      if (!this.dataset.serviceId || this.dataset.serviceAvailable !== 'true' || !(Number(this.dataset.servicePrice) > 0)) {
        throw new Error('Serviciul de printare nu este disponibil momentan. Contactează-ne pentru ajutor.');
      }
      const fields = [...this.querySelectorAll('input[type=file]')];
      if (![1, 2, 4].includes(fields.length)) throw new Error('Selectează un pachet de printare.');
      for (const field of fields) {
        const file = field.files[0];
        let message = '';
        if (!file) message = `Încarcă PDF-ul ${field.dataset.label}.`;
        else if (!/\.pdf$/i.test(file.name) || file.size === 0) message = 'Alege un fișier PDF valid, nu o imagine.';
        else if (file.size > 10 * 1024 * 1024) message = 'PDF-ul depășește 10 MB. Încarcă un fișier mai mic.';
        else {
          const header = await file.slice(0, 5).text();
          if (header !== '%PDF-') message = 'Fișierul nu are un antet PDF valid. Încarcă PDF-ul original.';
        }
        field.setCustomValidity(message);
        if (message) { field.reportValidity(); throw new Error(message); }
      }
      return fields;
    }
    buildPayload(fields) {
      const original = new FormData(this.form);
      const result = new FormData();
      const serviceOnly = this.dataset.mode.includes('service');
      const group = crypto.randomUUID();
      const serviceIndex = serviceOnly ? 0 : 1;
      if (!serviceOnly) {
        const id = original.get('id');
        const quantity = Number(original.get('quantity') || 1);
        if (!id || !Number.isInteger(quantity) || quantity < 1) throw new Error('Selectează varianta și cantitatea suportului.');
        result.append('items[0][id]', id);
        result.append('items[0][quantity]', String(quantity));
        for (const [key, value] of original.entries()) {
          if (/^properties\[.+\]$/.test(key)) result.append(`items[0][${key.replace('[', '][')}`, value);
          else if (key === 'selling_plan' && value) result.append('items[0][selling_plan]', value);
        }
        result.append('items[0][properties][_holdit_group]', group);
      }
      const prefix = `items[${serviceIndex}]`;
      result.append(`${prefix}[id]`, this.dataset.serviceId);
      result.append(`${prefix}[quantity]`, String(fields.length * 2));
      result.append(`${prefix}[properties][_holdit_pdf_count]`, String(fields.length));
      result.append(`${prefix}[properties][_holdit_group]`, group);
      result.append(`${prefix}[properties][Pachet printare]`, `${fields.length} PDF / ${fields.length * 2} ecusoane`);
      if (!serviceOnly) result.append(`${prefix}[properties][Pentru produs]`, this.dataset.productTitle);
      for (const field of fields) result.append(`${prefix}[properties][PDF ${field.dataset.label}]`, field.files[0], field.files[0].name);
      return { body: result, group };
    }
    async submit() {
      if (this.busy) return;
      this.busy = true;
      this.showError('');
      this.setAttribute('aria-busy', 'true');
      const status = this.querySelector('[data-holdit-status]');
      const buttons = [...this.form.querySelectorAll('[type=submit]')];
      const previous = buttons.map(button => button.disabled);
      buttons.forEach(button => { button.disabled = true; });
      const controls = [...this.querySelectorAll('select, input[type=file]')];
      controls.forEach(control => { control.disabled = true; });
      let requestStarted = false;
      try {
        const fields = await this.validateFiles();
        const { body, group } = this.buildPayload(fields);
        status.textContent = 'Se încarcă fișierele și se adaugă produsele în coș…';
        requestStarted = true;
        const response = await fetch(this.dataset.addUrl, {
          method:'POST', body, headers:{ Accept:'application/json' },
          credentials:'same-origin', signal:AbortSignal.timeout(120000),
        });
        const data = await response.json();
        if (!response.ok || data.status) throw new Error(typeof data.description === 'string' ? data.description : 'Produsele nu au putut fi adăugate.');
        const service = data.items?.find(item => String(item.variant_id || item.id) === this.dataset.serviceId && item.properties?._holdit_group === group);
        const filesSaved = service && fields.every(field => /^https?:\/\//.test(String(service.properties?.[`PDF ${field.dataset.label}`] || '')));
        if (!filesSaved || service.quantity !== fields.length * 2) throw new Error('Nu am putut confirma salvarea tuturor fișierelor și a cantității.');
        // Read the committed cart before navigating, not an early Horizon snapshot.
        const cartResponse = await fetch(`${this.dataset.cartUrl}.js`, {
          headers: { Accept: 'application/json' }, credentials: 'same-origin', cache: 'no-store',
          signal: AbortSignal.timeout(30000),
        });
        if (!cartResponse.ok) throw new Error('Nu am putut verifica actualizarea coșului.');
        const cart = await cartResponse.json();
        const added = cart.items?.filter(item => item.properties?._holdit_group === group) || [];
        const printLine = added.find(item => String(item.variant_id || item.id) === this.dataset.serviceId);
        const serviceOnly = this.dataset.mode.includes('service');
        const support = serviceOnly ? null : added.find(item => String(item.variant_id || item.id) === body.get('items[0][id]'));
        const validSupport = serviceOnly || support?.quantity === Number(body.get('items[0][quantity]'));
        const validFiles = printLine && fields.every(field => /^https?:\/\//.test(String(printLine.properties?.[`PDF ${field.dataset.label}`] || '')));
        if (added.length !== (serviceOnly ? 1 : 2) || !validSupport || !validFiles || printLine.quantity !== fields.length * 2) {
          throw new Error('Coșul nu confirmă produsele, fișierele și cantitățile selectate.');
        }
        status.textContent = 'Fișierele au fost adăugate. Se deschide coșul…';
        window.location.assign(this.dataset.cartUrl);
      } catch (error) {
        status.textContent = '';
        const detail = error.name === 'TimeoutError' ? 'Încărcarea durează prea mult.' : error.message;
        this.showError(requestStarted
          ? `${detail} Verifică întâi coșul: cererea poate fi procesată. Nu repeta adăugarea înainte să verifici produsele și fișierele.`
          : detail);
        this.querySelector('[data-holdit-cart-link]').hidden = !requestStarted;
      } finally {
        this.removeAttribute('aria-busy');
        // A submitted request is never retried blindly after an uncertain response.
        if (!requestStarted) {
          this.busy = false;
          buttons.forEach((button, index) => { button.disabled = previous[index]; });
          controls.forEach(control => { control.disabled = false; });
        }
      }
    }
  }
  customElements.define('holdit-product-options', HolditProductOptions);
})();
